import {lazyRegister} from '@/Core/lazyRegister';
import {BaseApiFactory} from "@/Factories/BaseApiFactory";
import {InvoiceSendResult, PaymentHistory} from "@/models";
import {
    OrderCustomerItemResult,
    OrderManagementDetailResult,
    OrderManagementItemResult,
    OrderManagementQuery
} from "@/models/OrderManagement";
import {ReturnOrderResult} from "@/models/ReturnProduct";

export class OrderService {
    static $inject = ['BaseApiFactory'];

    currentManagementQuery: OrderManagementQuery;

    constructor(private baseApiFactory: BaseApiFactory) {
    }

    listOrderByCustomerId(customerId: number, query: {
        page: number,
        size: number
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<OrderCustomerItemResult[]>('/v2/api/order/customer/' + customerId, query, cancelToken);
    };

    listOrderByFilter(query: OrderManagementQuery, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<OrderManagementItemResult[]>('/v2/api/order/filter', query, cancelToken);
    };

    getNextNumberByFilter(number: string, query: OrderManagementQuery, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<{
            PrevNumber?: string,
            NextNumber?: string
        }>(`/v2/api/order/${number}/next-item`, query, cancelToken, {
            silent: true
        });
    }

    GetOrderDetailsByNumber(number: string, langCode: string, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.getData<OrderManagementDetailResult>(`/api/Order/detail/${number}`, {langCode}, cancelToken);
    };

    RefreshStatus(postData, hideLoading, cancelToken: AbortSignal = null) {
        try {
            return this.baseApiFactory.post<boolean>('/api/Order/RefreshStatus', postData, cancelToken, {
                silent: hideLoading
            });
        } catch (err) {
            return Promise.resolve(err);
        }
    };

    CaptureOrder(postData) {
        return this.baseApiFactory.post<boolean>('/api/Order/CaptureOrder', postData);
    };

    CancelOrder(postData: {
        SapNumber: string,
        OrderId: number,
        DealerNumber: string
    }, cancelToken: AbortSignal = null) {
        return this.baseApiFactory.post<boolean>('/api/Order/CancelOrder', postData, cancelToken);
    };

    ManuallyReleaseOrder(postData: {
        SapNumber: string,
        OrderId: number,
        ReleaseReason: string
    }) {
        return this.baseApiFactory.post<boolean>('/api/Order/ManuallyReleaseOrder', postData);
    };

    ReleaseInStoreOrder(postData) {
        return this.baseApiFactory.post<boolean>('/api/Order/ReleaseInStoreOrder', postData);
    };

    Refund(postData) {
        return this.baseApiFactory.post<boolean>('/api/Order/refundOrder', postData);
    };

    GetReturnProducts(query) {
        return this.baseApiFactory.get<any[]>('/api/ReturnProduct?' + query);
    };

    ListReturnProducts(query, cancelToken: AbortSignal) {
        return this.baseApiFactory.getData<ReturnOrderResult[]>('/v2/api/ReturnProduct/ListByFilter', query, cancelToken);
    };

    UpdateReturnOrder(value) {
        return this.baseApiFactory.put('/api/ReturnProduct', value);
    };

    SendInvoiceEmail(number) {
        return this.baseApiFactory.post<InvoiceSendResult>('/api/Order/SendOrderInvoice', {number});
    };

    SendConfirmationEmail(orderId) {
        return this.baseApiFactory.post('/api/Order/SendOrderConfirmation', {id: orderId});
    };

    SendOrderToDistributor(orderId) {
        return this.baseApiFactory.post('/api/Order/SendOrderToDistributor', {id: orderId});
    };

    SendCreditNote(postData) {
        return this.baseApiFactory.post('/api/Order/SendCreditNote?' + "orderId=" + postData.orderId + "&sapNumber=" + postData.sapNumber + "&returnId=" + postData.returnId, {});
    };

    GetPaymentHistories(query: {
        orderId: number,
        countryCode: string
    }, cancelToken?: AbortSignal) {
        return this.baseApiFactory.getData<PaymentHistory[]>('/api/PaymentHistory', query, cancelToken);
    };

    AddPaymentHistoryManually(postData) {
        return this.baseApiFactory.post('/api/PaymentHistory/AddPaymentHistoryManually', postData);
    };

    ManuallyCaptured(postData) {
        return this.baseApiFactory.post('/api/Order/ManuallyCaptured', postData);
    };

    createSapOrderByOrderId(data) {
        return this.baseApiFactory.post<any>('/api/Order/CreateSapOrderByOrderId', data);
    };

    getCreditNotePdf(data, fileName: string) {
        return this.baseApiFactory.download('/api/ReturnProduct/GetCreditNote?' + data, {
            fileName: fileName + new Date().toISOString().split('T')[0]
                .replaceAll('-', '') + ".pdf"
        });
    };

    getInvoicePdf(sapNumber) {
        return this.baseApiFactory.download(`/api/Order/GetOrderInvoice/${sapNumber}`, {
            fileName: `Invoice-${sapNumber}.pdf`
        })
    }
}

lazyRegister.service('OrderService', OrderService);
